/**
 * Shared opportunity presentation — the durable pieces the search pipeline and the
 * opportunity detail both render (reuse, not copy).
 *
 * §0.1 Genre B: search results are a catalog of comparable EXTERNAL items, so the
 * BOUNDED item is the company group (`.opp-group` composing `.card-item`) and the
 * postings inside it are hairline rows. The company is the identity and the posting
 * title is the headline beneath it (P-B); `sourcePlatform` rides along as a quiet
 * »über …« attribution so a board-sourced posting can say where it was found without
 * lying about who is hiring, and the global logo renders only when one exists.
 *
 * Evaluation first (S9): the readout rail carries ONE compact score — the whole-percent
 * number, the human tier word and a meter whose COLOUR is the four-band ramp (never a
 * uniform teal). The stage word renders only while a result is NOT yet matched, where it
 * genuinely means "still working"; once graded, the tier readout says it alone. Material
 * exceptions ride as at-most-five signal tags — warnings are short `meh` LABELS with the
 * explanation on hover (and in full on the detail page), never ausformulierte prose.
 *
 * Bookmark / dismiss / delete / open-opportunity stay `data-act` verbs matching the api
 * command names (AX-3), each carrying the `data-opportunity` urn payload; the host
 * element owns the handlers.
 */

import { classMap, html, nothing, type TemplateResult } from "@platform/web-kit/render"
import type { CareerI18n, CareerKey } from "../../i18n"
import type {
  JobState,
  MatchTier,
  OpportunityCardView,
  OpportunityStage,
  OrgEvidenceView,
} from "../domain"
import {
  bandForStrength,
  signalTags,
  type OpportunityGroup,
  type SignalTag,
  type SignalTagCode,
} from "./opportunity-pipeline"

export const TIER_KEY: Record<MatchTier, CareerKey> = {
  strong: "opportunity.tier.strong",
  viable: "opportunity.tier.viable",
  weak: "opportunity.tier.weak",
  unsuited: "opportunity.tier.unsuited",
}
export const STAGE_KEY: Record<OpportunityStage, CareerKey> = {
  discovered: "opportunity.stage.discovered",
  verified: "opportunity.stage.verified",
  briefed: "opportunity.stage.briefed",
  matched: "opportunity.stage.matched",
}
// `FIT_KEY` is gone: the fit WORD is no longer rendered anywhere. `match` only restated the
// score, and `borderline` / `mismatch` are now warning tags that carry their own explanation
// (the `opportunity.flag.fit*` keys). The `opportunity.fit.*` catalog entries went with it —
// nothing rendered them any more, and a catalog key with no reader rots into a false promise
// that some surface still shows the word.
export const JOB_STATE_KEY: Record<JobState, CareerKey> = {
  open: "opportunity.jobState.open",
  closed: "opportunity.jobState.closed",
  unknown: "opportunity.jobState.unknown",
}
export const DIMENSION_KEY: Record<string, CareerKey> = {
  fit: "opportunity.dimension.fit",
  social: "opportunity.dimension.social",
  motivational: "opportunity.dimension.motivational",
  goal: "opportunity.dimension.goal",
}

/** Tag code → its short card label. */
const TAG_LABEL_KEY: Record<SignalTagCode, CareerKey> = {
  "not-real": "opportunity.flag.notReal",
  "source-suspicious": "opportunity.flag.sourceSuspicious",
  "fit-mismatch": "opportunity.flag.fitMismatch",
  "fit-borderline": "opportunity.flag.fitBorderline",
  closed: "opportunity.flag.closed",
  stale: "opportunity.flag.stale",
  "out-of-scope": "opportunity.flag.outOfScope",
  expired: "opportunity.flag.expired",
  deadline: "opportunity.flag.deadline",
  "anti-values": "opportunity.flag.antiValues",
  "low-confidence": "opportunity.flag.lowConfidence",
  "dim-fit": "opportunity.tag.fit",
  "dim-social": "opportunity.tag.social",
  "dim-motivational": "opportunity.tag.motivational",
  "dim-goal": "opportunity.tag.goal",
  salary: "opportunity.tag.salary",
}

/** Tag code → the one-sentence explanation (hover title on the card, full line on the detail). */
const TAG_HINT_KEY: Partial<Record<SignalTagCode, CareerKey>> = {
  "not-real": "opportunity.flag.notReal.hint",
  "source-suspicious": "opportunity.flag.sourceSuspicious.hint",
  "fit-mismatch": "opportunity.flag.fitMismatch.hint",
  "fit-borderline": "opportunity.flag.fitBorderline.hint",
  closed: "opportunity.flag.closed.hint",
  stale: "opportunity.flag.stale.hint",
  "out-of-scope": "opportunity.flag.outOfScope.hint",
  expired: "opportunity.flag.expired.hint",
  deadline: "opportunity.flag.deadline.hint",
  "anti-values": "opportunity.flag.antiValues.hint",
  "low-confidence": "opportunity.flag.lowConfidence.hint",
}

/** 0..1 → a whole-percent integer (AX-6: numbers ship with meaning). */
export const pct = (unit: number): number => Math.round(Math.max(0, Math.min(1, unit)) * 100)

/** The bare opportunity id behind a `urn:career:opportunity:{id}` urn (route param). */
export const opportunityShortId = (urn: string): string => urn.replace(/^urn:career:opportunity:/, "")

/** A deadline as the German short date the posting itself would print. */
const DEADLINE_FORMAT = new Intl.DateTimeFormat("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" })
export function formatDeadline(iso: string): string {
  const at = new Date(iso)
  return Number.isNaN(at.getTime()) ? iso : DEADLINE_FORMAT.format(at)
}

/** The interpolation a tag's label and hint share (plural `n`, the date, the named hits, a value). */
function tagParams(tag: SignalTag): Record<string, string | number> {
  return {
    ...(typeof tag.days === "number" ? { n: tag.days } : {}),
    ...(tag.date ? { date: formatDeadline(tag.date) } : {}),
    ...(tag.items && tag.items.length > 0 ? { items: tag.items.join(", ") } : {}),
    ...(tag.detail ? { value: tag.detail } : {}),
  }
}

/** One tag's short label — the same call the detail page reuses for its full-line list. */
export function tagLabel(tag: SignalTag, t: CareerI18n["t"]): string {
  return t(TAG_LABEL_KEY[tag.code], tagParams(tag))
}
/** One tag's explanation, or null when the tag needs none (a strength explains itself). */
export function tagHint(tag: SignalTag, t: CareerI18n["t"]): string | null {
  const key = TAG_HINT_KEY[tag.code]
  return key ? t(key, tagParams(tag)) : null
}

/**
 * The match readout — the whole-percent score, the human tier word and the ramped meter.
 * The `opp-tier--{tier}` class carries `--tier-hue`, so meter, word and (on the detail)
 * the hero accent all read ONE variable. Renders nothing before Matched.
 */
export function tierReadout(o: OpportunityCardView, t: CareerI18n["t"]): TemplateResult | typeof nothing {
  if (!o.tier) return nothing
  const score = typeof o.score === "number" ? pct(o.score) : null
  return html`<span class=${classMap({ "opp-tier": true, [`opp-tier--${o.tier}`]: true })} lang="de">
    ${score !== null ? html`<b class="opp-tier-score">${score}<span class="opp-tier-unit"> %</span></b>` : nothing}
    <span class="opp-tier-word">${t(TIER_KEY[o.tier])}</span>
    ${score !== null ? html`<span class="opp-tier-meter" aria-hidden="true"><i style=${`width:${score}%`}></i></span>` : nothing}
  </span>`
}

/** The signal-tag row (S9b + P-B): at most five, warnings first, each carrying its hover. */
export function signalTagRow(o: OpportunityCardView, t: CareerI18n["t"]): TemplateResult | typeof nothing {
  const tags = signalTags(o)
  if (tags.length === 0) return nothing
  return html`<ul class="opp-tags" role="list">
    ${tags.map((tag) => {
      const hint = tagHint(tag, t)
      return html`<li
        class=${classMap({ "opp-tag": true, [`opp-tag--${tag.kind}`]: true })}
        data-tag=${tag.code}
        title=${hint ?? nothing}
        lang="de"
        >${tagLabel(tag, t)}</li
      >`
    })}
  </ul>`
}

/**
 * The verify line — the facts that are still facts once the tags have taken the
 * judgements. `fit: "match"` and `jobState: "open"` are deliberately silent: the score
 * already said the first and the second is the default, and repeating them is exactly the
 * four-ways-the-same-thing chorus S9 removes. Only the salary and an unclear job state
 * remain a reading rather than a tag.
 */
function verifyLine(o: OpportunityCardView, t: CareerI18n["t"]): TemplateResult | typeof nothing {
  const parts: TemplateResult[] = []
  if (o.jobState === "unknown") parts.push(html`<span class="opp-jobstate">${t(JOB_STATE_KEY.unknown)}</span>`)
  if (o.location) parts.push(html`<span class="opp-location">${o.location}</span>`)
  if (parts.length === 0) return nothing
  return html`<p class="opp-verify" lang="de">${parts.map((p, i) => (i === 0 ? p : html`<span class="opp-dot" aria-hidden="true"> · </span>${p}`))}</p>`
}

/**
 * The lead line: the authored brief once present (UPGRADES in place); else the raw
 * posting snippet (domain data).
 */
function leadLine(o: OpportunityCardView, t: CareerI18n["t"]): TemplateResult | typeof nothing {
  if (o.brief) {
    return html`<div class=${classMap({ "opportunity-brief": true, "is-degraded": o.brief.degraded })}>
      ${o.brief.degraded ? html`<p class="opportunity-degraded-cue" role="status" lang="de">${t("opportunity.brief.degraded")}</p>` : nothing}
      <p class="opportunity-brief-text" lang="de">${o.brief.brief}</p>
      ${o.brief.fitNote ? html`<p class="opportunity-fitnote" lang="de">${o.brief.fitNote}</p>` : nothing}
    </div>`
  }
  return o.snippet ? html`<p class="opportunity-snippet" lang="de">${o.snippet}</p>` : nothing
}

/** Known employer-evidence kinds → their human source-badge label; unknown kinds show the raw kind. */
export const ORG_SOURCE_KEY: Record<string, CareerKey> = {
  kununu: "org.source.kununu",
  trustpilot: "org.source.trustpilot",
  company: "org.source.company",
  news: "org.source.news",
  posting: "org.source.posting",
}

/** One graded employer signal row: source badge · claim · attributed source (linked when a url is cited). */
function orgSignalRow(e: OrgEvidenceView, t: CareerI18n["t"]): TemplateResult {
  const key = ORG_SOURCE_KEY[e.kind]
  return html`<li class="opportunity-org-signal">
    <span class="org-source-badge" data-kind=${e.kind} lang="de">${key ? t(key) : e.kind}</span>
    <span class="org-signal-claim" lang="de">${e.claim}</span>
    ${e.url
      ? html`<a class="org-signal-source" href=${e.url} target="_blank" rel="noopener noreferrer" lang="de">${e.source}</a>`
      : html`<span class="org-signal-source" lang="de">${e.source}</span>`}
  </li>`
}

/**
 * The disclosable "Arbeitgeber" section (E3b): collapsed shows the synthesized one-line org
 * summary (keeps the list scannable); expanded shows the graded employer signals with source
 * badges. Renders NOTHING when the result carries no employer data — a gap is never dressed
 * up as a finding.
 */
function orgDisclosure(o: OpportunityCardView, t: CareerI18n["t"]): TemplateResult | typeof nothing {
  const org = o.org
  if (!org || (!org.summary && org.evidence.length === 0)) return nothing
  return html`
    <details class="opportunity-org-disclose">
      <summary class="opportunity-org-summary-line">
        <span class="opportunity-org-label" lang="de">${t("org.employer.label")}</span>
        <span class="opportunity-org-oneliner" lang="de">${org.summary ?? org.name}</span>
      </summary>
      ${org.evidence.length > 0
        ? html`<ul class="opportunity-org-signals" role="list">
            ${org.evidence.map((e) => orgSignalRow(e, t))}
          </ul>`
        : html`<p class="opportunity-org-empty surface-empty" lang="de">${t("org.signals.empty")}</p>`}
    </details>
  `
}

/** The user-verdict acts — bookmark (or the saved mark) · dismiss · delete, as quiet text affordances. */
function verdictActions(o: OpportunityCardView, t: CareerI18n["t"]): TemplateResult {
  return html`
    <div class="opportunity-actions">
      ${o.bookmarked
        ? html`<span class="opp-saved" lang="de">${t("opportunity.bookmarked")}</span>`
        : html`<button class="opp-act" data-act="bookmark" data-opportunity=${o.urn} lang="de">${t("opportunity.bookmark")}</button>`}
      <button class="opp-act" data-act="dismiss" data-opportunity=${o.urn} lang="de">${t("opportunity.dismiss")}</button>
      <button class="opp-act opp-act--remove" data-act="delete" data-opportunity=${o.urn} lang="de">${t("opportunity.delete")}</button>
      <!-- S11: the search card is a generate entry point. Offered only once the person has
           SAVED the Stelle — bookmarking is the verdict that makes writing worth the credits,
           and it is the same population the applications chooser draws from. The create verb
           comes LAST: a card is for judging first. -->
      ${o.bookmarked
        ? html`<button class="opp-act opp-act--generate" data-act="generate-application" data-opportunity=${o.urn} lang="de">${t("opportunity.generate")}</button>`
        : nothing}
    </div>
  `
}

/**
 * Render ONE posting as an `<li urn=…>` row (AX-4 urn-addressed). The company identity is
 * NOT repeated here — it is the group's band above (P-B: one company headline, then a
 * headline per posting).
 */
export function renderOpportunityCard(o: OpportunityCardView, t: CareerI18n["t"]): TemplateResult {
  const shortId = opportunityShortId(o.id)
  return html`
    <li
      urn=${o.urn}
      data-opportunity=${o.urn}
      class=${classMap({ "opportunity-card": true, [`is-${o.stage}`]: true, "is-bookmarked": o.bookmarked })}
    >
      <div class="opp-main">
        <a
          class="opportunity-title"
          href=${`/search/opportunities/${shortId}`}
          data-act="open-opportunity"
          data-opportunity=${o.urn}
          lang="de"
          >${o.title}</a
        >
        ${verifyLine(o, t)}
        ${signalTagRow(o, t)}
        ${leadLine(o, t)}
        ${orgDisclosure(o, t)}
        ${verdictActions(o, t)}
      </div>
      <div class="opp-rail">
        ${o.stage === "matched" ? nothing : html`<span class="opportunity-stage" lang="de">${t(STAGE_KEY[o.stage])}</span>`}
        ${tierReadout(o, t)}
      </div>
    </li>
  `
}

/**
 * The company band — the identity the postings hang under. The logo is an ENHANCEMENT:
 * roughly three in five companies yield one (WS-E), so the band must read as complete
 * without it and the layout never reserves its space. `sourcePlatform` becomes the quiet
 * »über LinkedIn« attribution, which is only honest now that `employer` is the HIRING
 * company rather than the board's host.
 */
function companyBand(group: OpportunityGroup, t: CareerI18n["t"]): TemplateResult {
  const { company, items } = group
  const meta: TemplateResult[] = []
  if (company.sourcePlatform) meta.push(html`<span class="opp-company-via" lang="de">${t("search.company.via", { platform: company.sourcePlatform })}</span>`)
  if (items.length > 1) meta.push(html`<span class="opp-company-count" lang="de">${t("search.company.postings", { n: items.length })}</span>`)
  return html`
    <header class="opp-company">
      ${company.logoUrl
        ? html`<img class="opp-company-logo" src=${company.logoUrl} alt=${t("search.company.logo", { name: company.name })} loading="lazy" decoding="async" />`
        : nothing}
      <div class="opp-company-id">
        <p class="opp-company-name" lang="de">${company.name}</p>
        ${meta.length > 0
          ? html`<p class="opp-company-meta" lang="de">
              ${meta.map((m, i) => (i === 0 ? m : html`<span class="opp-company-sep" aria-hidden="true">·</span>${m}`))}
            </p>`
          : nothing}
      </div>
    </header>
  `
}

/**
 * Render one company group as the bounded Genre-B item: the company band once, then its
 * postings as hairline rows. A group of one renders the same way — the company is the
 * identity whether it has one posting or five (P-B) — and only the count is dropped.
 */
export function renderOpportunityGroup(group: OpportunityGroup, t: CareerI18n["t"]): TemplateResult {
  return html`
    <li class="opp-group card-item" data-company=${group.key}>
      ${companyBand(group, t)}
      <ul class="opp-postings" role="list">
        ${group.items.map((o) => renderOpportunityCard(o, t))}
      </ul>
    </li>
  `
}

/** Re-export so surfaces reach the ramp through the presentation module they already import. */
export { bandForStrength }
